/**
 * EvidenceRetentionNoticeDialog — what the citizen is told when a deletion
 * does not reach a company's decision record (W5.8 · Art. 12(4)).
 *
 * For each company: WHO keeps it (`companyName`), WHAT (`kindLabelKeys`), UNTIL
 * WHEN (`retainUntil`, or — while the company's decision is still in force —
 * no end date yet) and WHY (`reasonKey`). Every one of those is the backend's
 * `EvidenceRetentionNotice`, rendered as sent; nothing here decides whether a
 * company may keep something or for how long.
 *
 * `context` only changes the opening sentence: a single file left the vault,
 * or the whole account was deleted.
 */
import { useTranslation, useLocale } from '@tonnie/locale';
import type { EvidenceRetentionNotice } from '@tonnie/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tonnie/ui';
import { Building2 } from '@tonnie/assets';

export type EvidenceRetentionContext = 'file' | 'account';

export interface EvidenceRetentionNoticeDialogProps {
  notices: EvidenceRetentionNotice[];
  context: EvidenceRetentionContext;
  open: boolean;
  onClose: () => void;
}

const INTRO_KEYS = {
  file: 'evidence.notice.introFile',
  account: 'evidence.notice.introAccount',
} as const satisfies Record<EvidenceRetentionContext, string>;

export function EvidenceRetentionNoticeDialog({
  notices,
  context,
  open,
  onClose,
}: EvidenceRetentionNoticeDialogProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent
        closeLabel={t('common.close')}
        data-testid="evidence-retention-notice"
        data-context={context}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{t('evidence.notice.title')}</DialogTitle>
          <DialogDescription>{t(INTRO_KEYS[context])}</DialogDescription>
        </DialogHeader>

        <ul
          className="max-h-[50vh] space-y-3 overflow-y-auto"
          aria-label={t('evidence.notice.listLabel')}
        >
          {notices.map((notice) => (
            <li
              key={notice.companyTenantId}
              data-testid={`evidence-retention-notice-${notice.companyTenantId}`}
              data-basis={notice.basis}
              className="rounded-lg border border-border bg-muted/30 p-3"
            >
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                {notice.companyName}
              </p>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-muted-foreground">{t('evidence.notice.what')}</dt>
                <dd className="text-foreground">
                  <ul>
                    {notice.kindLabelKeys.map((key) => (
                      <li key={key}>{t(key)}</li>
                    ))}
                  </ul>
                </dd>
                <dt className="text-muted-foreground">{t('evidence.notice.until')}</dt>
                <dd className="text-foreground">
                  {notice.retainUntil
                    ? formatDate(notice.retainUntil)
                    : t('evidence.notice.noEndDate')}
                </dd>
                <dt className="text-muted-foreground">{t('evidence.notice.why')}</dt>
                <dd className="text-foreground">{t(notice.reasonKey)}</dd>
              </dl>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button type="button" onClick={onClose} data-testid="evidence-retention-notice-close">
            {t('evidence.notice.acknowledge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
